/**
 * EK-Berechnung nach dem Einkaufs-Workflow (Einstandskosten je Position) - 1:1-Portierung von
 * `calcEk` aus `index.html:10038-10263` (Stand 2026-08-09). Der Rechenweg ist woertlich uebernommen,
 * auch wo er umstaendlich wirkt oder auf den ersten Blick fehlerhaft aussieht; ergaenzt wurden
 * ausschliesslich Typen. Namen, Reihenfolge der Zuweisungen, Klammerung und Operandenreihenfolge
 * sind unangetastet - bei 225 Zeilen Gleitkommarechnung IST die Auswertungsreihenfolge das Ergebnis.
 *
 * Der Original-Kommentar ueber der Funktion, unveraendert:
 *
 * > Separate FOC-Zeilen werden ihrer Haupt-SKU zugerechnet und nicht selbst kalkuliert.
 * > Manual-, Verpackungs- und Zubehoerpositionen mit Manual-Flag werden vollstaendig ignoriert.
 * > PL-Menge gewinnt; Fracht nach Warenwert; Zoll innerhalb einer Tarifgruppe nach Warenwert;
 * > EK neu ist der gewichtete Mischpreis aus Altbestand und neuer Lieferung.
 *
 * ================= Der Umbau: DB-Bindung -> Argument =================
 *
 * `calcEk` ruft im Original vier globale Funktionen auf, von denen zwei globalen Zustand lesen:
 * `orderCurrency(o)` (ueber `orderSupplier` -> `DB.suppliers`) und `fxMix(o)` (ueber `orderFx` ->
 * `DB.fxDeals`). Hier bekommen sie die jeweilige Liste als Argument: `calcEk(o, deals, suppliers)`,
 * aufgeloeste Datenlisten am Ende in der Reihenfolge deals -> suppliers. `definitionen` wird nicht
 * gebraucht - keine der vier Abhaengigkeiten verlangt es.
 *
 * ================= Keine Zeitabhaengigkeit =================
 *
 * Weder `calcEk` selbst noch eine ihrer vier Abhaengigkeiten ruft `todayIso()` oder `new Date()` auf
 * (eigens nachgeprueft, nicht dem Task-Brief geglaubt: `orderCurrency` -> `orderSupplier` ->
 * `supplierById`/`supplierByName` -> `allSuppliers` -> `supplierKey`; `orderTotal` ->
 * `calculatedOrderTotal`; `fxMix` -> `orderFx` -> `dealValid`; `paymentUsdRate` -> `payAmount` ->
 * `orderTotal`). Das Ergebnis ist damit vollstaendig fixture-tauglich. (Richtiggestellt in Task 8:
 * die Kette liess zuvor `allSuppliers` aus, das `supplierByName` tatsaechlich aufruft - Befund aus
 * dem Task-7a-Review.)
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `typeof paymentUsdRate === 'function' ? paymentUsdRate(o) : 0` (10048) ist eine Absicherung
 *    gegen eine Ladereihenfolge, die es im Modulsystem nicht mehr gibt: bei einem Import ist der
 *    Zweig immer wahr. Der `else`-Zweig ist trotzdem Verhalten und bleibt stehen - die
 *    Woertlichkeitsregel schlaegt die Aufraeumung.
 * 2. Der Rueckfall auf die Vorgaengerzeile (10073-10078) prueft nur `!foc`, nicht `!manual`. Eine
 *    FOC-Zeile hinter einer `manual`-Zeile wird deshalb an einen Index gehaengt, den `rows` gar
 *    nicht kennt; ihre Menge verschwindet aus `focQtySum` UND aus `unassignedFocQty`. Woertlich
 *    uebernommen, im Parity-Test festgehalten.
 * 3. Der Rueckfall "einzige Hauptzeile" (10080) setzt `target = mainItems[0]` - ein Eintrag OHNE
 *    `sku`, waehrend alle anderen Zweige einen mit `sku` liefern. Folgenlos (nur `target.i` wird
 *    gelesen), aber nicht vereinheitlicht.
 * 4. Zeile und Tarifgruppe verweisen aufeinander (`row.customsGroup = group` und
 *    `group.rows.push(row)`) - das Ergebnis ist zyklisch. Woertlich uebernommen; die Testseite loest
 *    den Zyklus fuer den Vergleich auf (siehe `test/helpers/ekEingaben.ts`).
 * 5. Die Schluesselreihenfolge der Zeilenobjekte haengt daran, in welcher Reihenfolge die
 *    Abschnitte ihre Felder zuweisen - Zeilen mit Tarifgruppe fuehren `customsGroup`, Zeilen ohne
 *    Tarif haben den Schluessel gar nicht. Die Portierung weist in derselben Reihenfolge zu.
 *
 * ================= Kein Importzyklus =================
 *
 * Dieses Modul importiert aus `fx.ts`, `payments.ts` und `suppliers.ts`; keines davon importiert
 * `landedCost.ts`. `calcEk` ist damit das Blatt, nicht der Knoten.
 */
import { fxMix, paymentUsdRate, type FxDeal } from "./fx.ts";
import { orderTotal } from "./payments.ts";
import { orderCurrency, type Supplier } from "./suppliers.ts";
import { normSearch } from "./format.ts";
import type { StatusDefinitionen } from "./status.ts";

// ================= Typen =================

// Die Order-Typdeklaration dieses Moduls steht seit Task 1 gebuendelt in `lib/types.ts`
// (Begruendung dort). Re-Export unter dem bisherigen Namen, damit kein bestehender Importpfad bricht.
import type { Order } from "./types.ts";
export type { Order };

/** Eine Bestellposition (`order.items[]`). */
export interface EkItem {
  [feld: string]: unknown;
}

/** Eine FOC-Zeile samt ihrer abgeleiteten Menge - Wert von `focLinks` und `unassignedFoc`. */
export interface FocLink {
  it: EkItem;
  i: number;
  qty: number;
  sku: string;
  qtyPlIsSet: boolean;
}

/**
 * Eine Zolltarifgruppe. `rows`/`totals` entstehen beim Sammeln, `total`/`conflict` erst in der
 * Auswertungsschleife danach - die Schluesselreihenfolge des Objekts folgt daraus.
 */
export interface EkTariffGroup {
  rows: EkRow[];
  totals: number[];
  total: number;
  conflict: boolean;
}

/**
 * Eine berechnete Positionszeile. Die Reihenfolge der Felder entspricht der Reihenfolge, in der das
 * Original sie zuweist: erst das Objektliteral (bis `tariffKey`), dann `share`, die Fracht- und
 * Werkzeuganteile, die Zollfelder und zuletzt die EK-Rechnung.
 *
 * `customsGroup` ist optional, weil Zeilen ohne `customsTariff` den Schluessel im Original gar nicht
 * bekommen.
 */
export interface EkRow {
  it: EkItem;
  i: number;
  orderedQty: number;
  directFocQty: number;
  directFocIncluded: number;
  linkedFoc: FocLink[];
  linkedFocQty: number;
  focOverrideIsSet: boolean;
  focInputValue: unknown;
  focQty: number;
  qtyPl: number;
  qtyPlIsSet: boolean;
  qtyEffAuto: number;
  qtyEffOverrideIsSet: boolean;
  qtyEff: number;
  sum: number;
  positionShare: number;
  supplierShare: number;
  supplierShareEur: number;
  rate: number;
  noRate: boolean;
  ekOriginal: number;
  tariffKey: string;
  share: number;
  freightShare: number;
  toolingShare: number;
  freightFeesShare: number;
  customsGroup?: EkTariffGroup;
  customsShare: number;
  customsMode: string;
  dutyPctActual: number;
  dutyPctJtl: number;
  dutyMismatch: boolean;
  ekDeAuto: number;
  ekDeOverrideIsSet: boolean;
  ekDe: number;
  stockOld: number;
  ekOld: number;
  hasOld: boolean;
  ekNewAuto: number;
  ekNewOverrideIsSet: boolean;
  ekNew: number;
}

/** Das Ergebnis von `calcEk` - Reihenfolge wie im Original-Rueckgabeliteral (10258-10262). */
export interface CalcEkResult {
  isUsd: boolean;
  noRate: boolean;
  positionGoods: number;
  supplierInvoice: number;
  goodsEur: number;
  customs: number;
  freight: number;
  freightFees: number;
  tooling: number;
  rows: EkRow[];
  qtySum: number;
  focQtySum: number;
  unassignedFoc: FocLink[];
  unassignedFocQty: number;
  landedBase: number;
  landed: number;
  manualEkAdjustment: number;
}

/** Ein Zuordnungsziel einer FOC-Zeile. `sku` fehlt im Rueckfall "einzige Hauptzeile" (Eigenheit 3). */
interface FocZiel {
  it: EkItem;
  i: number;
  sku?: string;
}

// ================= calcEk =================

/** index.html:10038 - `DB.fxDeals` ist hier `deals`, `DB.suppliers` ist `suppliers`. */
export function calcEk(
  o: Order,
  deals: readonly FxDeal[] | null | undefined,
  suppliers: readonly Supplier[] | null | undefined,
): CalcEkResult {
  const isUsd = orderCurrency(o, suppliers) === "USD";
  const items = (o.items || []) as EkItem[];
  const positionGoods = orderTotal(o);
  const legacySupplierInvoice = Number(o.supplierInvoice) > 0 ? Number(o.supplierInvoice) : 0;
  const legacyCustoms = Number(o.customsInvoice) || 0;
  const legacyFreight = Number(o.freightInvoice) || Number(o.freight) || 0;
  const legacyFreightFees = Number(o.freightFees) || 0;
  const legacyTooling = Number(o.toolingCost) || 0;
  const automaticFxRate = fxMix(o, deals).rate;
  // Eigenheit 1 im Kopfkommentar: der `else`-Zweig ist im Modulsystem unerreichbar, bleibt aber
  // woertlich stehen.
  const statusPaymentRate = typeof paymentUsdRate === "function" ? paymentUsdRate(o) : 0;
  const hasValue = (value: unknown): boolean =>
    value !== "" && value !== null && value !== undefined && Number.isFinite(Number(value));
  const positive = (value: unknown): number => Math.max(0, Number(value) || 0);
  const skuKey = (value: unknown): string =>
    String(value || "").trim().replace(/\s+/g, "").toUpperCase();
  const mainItems = items
    .map((it, i) => ({ it, i }))
    .filter((entry) => !entry.it.foc && !entry.it.manual);
  const mainSkuEntries = mainItems
    .map((entry) => ({ ...entry, sku: skuKey(entry.it.sku) }))
    .filter((entry) => entry.sku);
  const focLinks = new Map<number, FocLink[]>();
  const unassignedFoc: FocLink[] = [];

  for (let focIndex = 0; focIndex < items.length; focIndex++) {
    const focItem = items[focIndex];
    if (!focItem?.foc) continue;
    const focSku = skuKey(focItem.focForSku || focItem.sku);
    const baseSku = focSku.replace(/(?:[-_]?FOC)$/i, "");
    let target: FocZiel | undefined = mainSkuEntries.find((entry) => entry.sku === baseSku);
    if (!target && baseSku) {
      target = mainSkuEntries
        .filter((entry) => String(focItem.name || "").toUpperCase().includes(entry.sku))
        .sort((a, b) => b.sku.length - a.sku.length)[0];
    }
    if (!target) {
      for (let previous = focIndex - 1; previous >= 0; previous--) {
        if (!items[previous]?.foc) {
          target = { it: items[previous], i: previous, sku: skuKey(items[previous].sku) };
          break;
        }
      }
    }
    if (!target && mainItems.length === 1) target = mainItems[0];

    const focQtyPlIsSet = hasValue(focItem.qtyPl);
    const focQty = focQtyPlIsSet
      ? positive(focItem.qtyPl)
      : positive(focItem.qty) + positive(focItem.focQty);
    const link: FocLink = {
      it: focItem,
      i: focIndex,
      qty: focQty,
      sku: skuKey(focItem.sku),
      qtyPlIsSet: focQtyPlIsSet,
    };
    if (target) {
      if (!focLinks.has(target.i)) focLinks.set(target.i, []);
      (focLinks.get(target.i) as FocLink[]).push(link);
    } else {
      unassignedFoc.push(link);
    }
  }

  const calculationItems = mainItems.map((entry) => entry.it);
  const explicitSupplierTotal = calculationItems.reduce(
    (s, it) => s + (hasValue(it.supplierInvoice) ? positive(it.supplierInvoice) : 0),
    0,
  );
  const unfilledPositionGoods = calculationItems.reduce(
    (s, it) => s + (!hasValue(it.supplierInvoice) ? positive(it.qty) * positive(it.price) : 0),
    0,
  );
  const supplierRemainder = Math.max(0, legacySupplierInvoice - explicitSupplierTotal);

  const rows = mainItems.map(({ it, i }) => {
    const orderedQty = positive(it.qty);
    const sum = orderedQty * positive(it.price);
    const positionShare =
      positionGoods > 0 ? sum / positionGoods : (mainItems.length ? 1 / mainItems.length : 0);
    const supplierShare = hasValue(it.supplierInvoice)
      ? positive(it.supplierInvoice)
      : (legacySupplierInvoice > 0
          ? (unfilledPositionGoods > 0 ? supplierRemainder * sum / unfilledPositionGoods : 0)
          : sum);
    const rate =
      Number(it.mixedUsdRate) ||
      Number(it.usdRate) ||
      automaticFxRate ||
      statusPaymentRate ||
      Number(o.mixedUsdRate) ||
      Number(o.usdRate) ||
      0;
    const noRate = isUsd && rate <= 0;
    const supplierShareEur = isUsd ? (rate > 0 ? supplierShare / rate : 0) : supplierShare;
    const directFocQty = positive(it.focQty);
    const linkedFoc = focLinks.get(i) || [];
    const linkedFocQty = linkedFoc.reduce((s, link) => s + link.qty, 0);
    const qtyPlIsSet = hasValue(it.qtyPl);
    const qtyPl = qtyPlIsSet ? positive(it.qtyPl) : 0;
    const focOverrideIsSet = linkedFocQty > 0 && hasValue(it.focQty);
    const directFocIncluded = linkedFocQty > 0
      ? (focOverrideIsSet ? directFocQty : 0)
      : (qtyPlIsSet ? 0 : directFocQty);
    const focQty = linkedFocQty > 0
      ? (focOverrideIsSet ? directFocQty : linkedFocQty)
      : directFocIncluded;
    const focInputValue = linkedFocQty > 0 && !focOverrideIsSet
      ? linkedFocQty
      : (it.focQty ?? "");
    const qtyEffAuto = (qtyPlIsSet ? qtyPl : orderedQty) + focQty;
    const qtyEffOverrideIsSet = hasValue(it.qtyEffOverride);
    const qtyEff = qtyEffOverrideIsSet ? positive(it.qtyEffOverride) : qtyEffAuto;
    const ekOriginal = qtyEff > 0 ? supplierShare / qtyEff : 0;
    // Das Original legt die Zeile mit genau diesen 23 Schluesseln an und ergaenzt die uebrigen 17
    // erst in den Abschnitten darunter. Die Schluesselreihenfolge des Ergebnisses haengt daran; der
    // Cast haelt sie fest, ohne das Laufzeitverhalten zu beruehren.
    return {
      it,
      i,
      orderedQty,
      directFocQty,
      directFocIncluded,
      linkedFoc,
      linkedFocQty,
      focOverrideIsSet,
      focInputValue,
      focQty,
      qtyPl,
      qtyPlIsSet,
      qtyEffAuto,
      qtyEffOverrideIsSet,
      qtyEff,
      sum,
      positionShare,
      supplierShare,
      supplierShareEur,
      rate,
      noRate,
      ekOriginal,
      tariffKey: String(it.customsTariff || "").replace(/\s+/g, "").toUpperCase(),
    } as unknown as EkRow;
  });

  const allocationTotal = rows.reduce((s, r) => s + r.supplierShare, 0);
  for (const r of rows) {
    r.share = allocationTotal > 0 ? r.supplierShare / allocationTotal : r.positionShare;
  }

  // Manuelle Artikelanteile werden vom Gesamtbetrag abgezogen; nur der Rest wird automatisch verteilt.
  const allocateRemainder = (
    itemKey: "freightInvoice" | "toolingCost",
    total: number,
    resultKey: "freightShare" | "toolingShare",
  ): void => {
    const manualTotal = rows.reduce(
      (s, r) => s + (hasValue(r.it[itemKey]) ? positive(r.it[itemKey]) : 0),
      0,
    );
    const automaticRows = rows.filter((r) => !hasValue(r.it[itemKey]));
    const automaticGoods = automaticRows.reduce((s, r) => s + r.supplierShare, 0);
    const remainder = Math.max(0, total - manualTotal);
    for (const r of rows) {
      r[resultKey] = hasValue(r.it[itemKey])
        ? positive(r.it[itemKey])
        : (automaticGoods > 0 ? remainder * r.supplierShare / automaticGoods : 0);
    }
  };
  allocateRemainder("freightInvoice", legacyFreight, "freightShare");
  allocateRemainder("toolingCost", legacyTooling, "toolingShare");
  for (const r of rows) r.freightFeesShare = legacyFreightFees * r.share;

  // Zolltarifgruppen: Der Betrag aus dem Einfuhrabgabenbescheid wird pro Tarif einmal erfasst
  // und innerhalb der Gruppe proportional zum Warenwert verteilt.
  const tariffGroups = new Map<string, EkTariffGroup>();
  for (const r of rows) {
    if (!r.tariffKey) continue;
    // Das Original legt die Gruppe mit genau zwei Schluesseln an; `total`/`conflict` kommen erst in
    // der Schleife darunter dazu. Der Cast haelt das fest (siehe Kommentar bei den Zeilen oben).
    if (!tariffGroups.has(r.tariffKey)) {
      tariffGroups.set(r.tariffKey, { rows: [], totals: [] } as unknown as EkTariffGroup);
    }
    const group = tariffGroups.get(r.tariffKey) as EkTariffGroup;
    group.rows.push(r);
    if (hasValue(r.it.customsGroupAmount) && positive(r.it.customsGroupAmount) > 0) {
      group.totals.push(positive(r.it.customsGroupAmount));
    }
  }
  let tariffCovered = 0;
  for (const group of tariffGroups.values()) {
    group.total = group.totals[0] || 0;
    group.conflict = group.totals.some((value) => Math.abs(value - group.total) > 0.01);
    const manualTotal = group.rows.reduce(
      (s, r) => s + (hasValue(r.it.customsInvoice) ? positive(r.it.customsInvoice) : 0),
      0,
    );
    const automaticRows = group.rows.filter((r) => !hasValue(r.it.customsInvoice));
    const automaticGoods = automaticRows.reduce((s, r) => s + r.supplierShare, 0);
    const remainder = Math.max(0, group.total - manualTotal);
    if (group.total > 0) tariffCovered += group.total;
    for (const r of group.rows) {
      r.customsGroup = group;
      if (hasValue(r.it.customsInvoice)) {
        r.customsShare = positive(r.it.customsInvoice);
        r.customsMode = "manual";
      } else if (group.total > 0) {
        r.customsShare = automaticGoods > 0 ? remainder * r.supplierShare / automaticGoods : 0;
        r.customsMode = "tariff";
      }
    }
  }

  const customsManualOutsideGroups = rows.reduce(
    (s, r) =>
      s +
      (hasValue(r.it.customsInvoice) && (!r.customsGroup || r.customsGroup.total <= 0)
        ? positive(r.it.customsInvoice)
        : 0),
    0,
  );
  // `r.customsShare` ist an dieser Stelle fuer Zeilen ausserhalb einer Tarifgruppe noch gar nicht
  // gesetzt; der Typ fuehrt das Feld als `number`, weil am Ende jede Zeile einen Wert hat. Der Cast
  // macht die Undefiniert-Pruefung des Originals typseitig moeglich, ohne sie zu veraendern.
  const customsFallbackRows = rows.filter(
    (r) => (r.customsShare as number | undefined) === undefined && !hasValue(r.it.customsInvoice),
  );
  const customsFallbackGoods = customsFallbackRows.reduce((s, r) => s + r.supplierShare, 0);
  const customsFallback = Math.max(0, legacyCustoms - tariffCovered - customsManualOutsideGroups);

  for (const r of rows) {
    if ((r.customsShare as number | undefined) === undefined) {
      if (hasValue(r.it.customsInvoice)) {
        r.customsShare = positive(r.it.customsInvoice);
        r.customsMode = "manual";
      } else {
        r.customsShare =
          customsFallbackGoods > 0 ? customsFallback * r.supplierShare / customsFallbackGoods : 0;
        r.customsMode = customsFallback > 0 ? "fallback" : "none";
      }
    }
    r.dutyPctActual = r.supplierShareEur > 0 ? r.customsShare / r.supplierShareEur * 100 : 0;
    r.dutyPctJtl = hasValue(r.it.dutyPctJtl) ? positive(r.it.dutyPctJtl) : positive(o.dutyPct);
    r.dutyMismatch =
      r.dutyPctActual > 0 && r.dutyPctJtl > 0 && Math.abs(r.dutyPctActual - r.dutyPctJtl) > 0.05;
    r.ekDeAuto = r.qtyEff > 0
      ? (r.supplierShareEur + r.customsShare + r.freightShare + r.freightFeesShare + r.toolingShare) /
        r.qtyEff
      : 0;
    r.ekDeOverrideIsSet = hasValue(r.it.ekDeOverride);
    r.ekDe = r.ekDeOverrideIsSet ? positive(r.it.ekDeOverride) : r.ekDeAuto;
    r.stockOld = positive(r.it.stockOld);
    r.ekOld = positive(r.it.ekOld);
    r.hasOld = r.stockOld > 0 && r.ekOld > 0;
    r.ekNewAuto = r.qtyEff > 0
      ? (r.hasOld
          ? (r.stockOld * r.ekOld + r.qtyEff * r.ekDe) / (r.stockOld + r.qtyEff)
          : r.ekDe)
      : 0;
    r.ekNewOverrideIsSet = hasValue(r.it.ekNewOverride);
    r.ekNew = r.ekNewOverrideIsSet ? positive(r.it.ekNewOverride) : r.ekNewAuto;
  }

  const qtySum = rows.reduce((s, r) => s + r.qtyEff, 0);
  const focQtySum = rows.reduce((s, r) => s + r.focQty, 0);
  const supplierInvoice = rows.reduce((s, r) => s + r.supplierShare, 0);
  const goodsEur = rows.reduce((s, r) => s + r.supplierShareEur, 0);
  const customs = rows.reduce((s, r) => s + r.customsShare, 0);
  const freight = rows.reduce((s, r) => s + r.freightShare, 0);
  const freightFees = rows.reduce((s, r) => s + r.freightFeesShare, 0);
  const tooling = rows.reduce((s, r) => s + r.toolingShare, 0);
  const noRate = rows.some((r) => r.noRate && r.supplierShare > 0);
  const landedBase = goodsEur + customs + freight + freightFees + tooling;
  const hasEkDeOverrides = rows.some((r) => r.ekDeOverrideIsSet);
  const landed = hasEkDeOverrides ? rows.reduce((s, r) => s + r.qtyEff * r.ekDe, 0) : landedBase;
  const manualEkAdjustment = landed - landedBase;
  const unassignedFocQty = unassignedFoc.reduce((s, link) => s + link.qty, 0);
  return {
    isUsd,
    noRate,
    positionGoods,
    supplierInvoice,
    goodsEur,
    customs,
    freight,
    freightFees,
    tooling,
    rows,
    qtySum,
    focQtySum,
    unassignedFoc,
    unassignedFocQty,
    landedBase,
    landed,
    manualEkAdjustment,
  };
}

// ================= Begleitfunktionen (Task 7b) =================
//
// Sechs weitere Funktionen aus dem Einkaufs-Workflow, dazu ein Zulieferer (`cloneData`) und ein
// modulinterner Helfer (`byId`) - beide ohne eigenen Export, genau wie `allocateRemainder` in
// `calcEk` oben.
//
// ================= Der Umbau: DB-Bindung -> Argument =================
//
// `splitRootOrderId` und `shipmentOrderGroup` lesen im Original `DB.orders` (ueber `byId` bzw.
// direkt); `documentItemAllocations` liest es ebenfalls ueber `byId`. Alle drei bekommen die Liste
// hier als zusaetzliches Argument `alleBestellungen` - bei `documentItemAllocations` als VIERTER
// Parameter, weil der bereits vorhandene Parameter `orders` die AUSGEWAEHLTE Bestellliste ist, nicht
// `DB.orders` (siehe Task-Brief). `articleStats` liest `DB.orders` direkt (erster Parameter `orders`)
// und ruft (bis zur Statusbereinigung) `isSplitStatusOrder(o, definitionen)` auf - `definitionen`
// steht deshalb unmittelbar hinter dem Hauptparameter, wie in `lib/status.ts` bereits viermal
// (`boardGroupId` u.a.). `isSplitStatusOrder` ist als gemeinsamer Export aus `lib/status.ts`
// entfallen (der Board-Status "split" existiert in der LIVE-Konfiguration nicht mehr); `articleStats`
// ist aber eine woertliche 1:1-Portierung von index.html:14216-14253 und muss dessen dortiges,
// UNVERAENDERTES Ausschlussverhalten weiterhin exakt replizieren (Parity-Test gegen
// `altBegleit.articleStats()`, `test/parity/landedCost.test.ts`) - die Pruefung lebt deshalb ab
// jetzt als private Kopie in diesem Modul (`isSplitStatusOrderPortiert` unten), nicht mehr als
// gemeinsamer Import.
// `distributeDocumentAmount` und `consolidatedItems` brauchen keine Aufloesung: ersteres nutzt nur
// `orderTotal` (aus `payments.ts`), zweiteres nur `normSearch` (aus `format.ts`) und `cloneData`.
//
// ================= cloneData (index.html:5874) =================

/**
 * `value => JSON.parse(JSON.stringify(value))`. Modulintern, von `consolidatedItems` gebraucht.
 * Wirft weg, was `undefined` ist, und macht aus `-0` eine `0` - Bestand, bleibt so (siehe
 * Kopfkommentar der Testfixtures).
 */
const cloneData = (value: unknown): unknown => JSON.parse(JSON.stringify(value));

/** index.html:4692 - modulintern von `splitRootOrderId`/`documentItemAllocations` gebraucht; im Original liest sie `DB.orders`, hier kommt die Liste als Argument. */
const byId = (id: unknown, alleBestellungen: readonly Order[]): Order | undefined =>
  alleBestellungen.find((o) => o.id === id);

// ================= splitRootOrderId (index.html:12233-12242) =================

/** `DB.orders` (ueber `byId`) ist hier `alleBestellungen`. */
export function splitRootOrderId(order: Order, alleBestellungen: readonly Order[]): string {
  let current: Order = order;
  const seen = new Set<unknown>();
  while (current?.splitFrom && !seen.has(current.id)) {
    seen.add(current.id);
    current = byId(current.splitFrom, alleBestellungen) || current;
    if (current.id === order.id) break;
  }
  return (current?.id || order?.id || "") as string;
}

// ================= shipmentOrderGroup (index.html:12244-12250) =================

/** `DB.orders` ist hier `alleBestellungen`. */
export function shipmentOrderGroup(
  order: Order | null | undefined,
  alleBestellungen: readonly Order[],
): Order[] {
  if (!order) return [];
  const rootId = splitRootOrderId(order, alleBestellungen);
  return alleBestellungen
    .filter((candidate) => splitRootOrderId(candidate, alleBestellungen) === rootId)
    .sort((a, b) =>
      a.id === rootId
        ? -1
        : b.id === rootId
          ? 1
          : String(a.orderNo || "").localeCompare(String(b.orderNo || ""), "de"),
    );
}

// ================= distributeDocumentAmount (index.html:12252-12264, unveraendert) =================

/** Ein Anteil aus `distributeDocumentAmount` - Reihenfolge wie im Original-Objektliteral. */
export interface DocumentShare {
  orderId: unknown;
  amount: number;
}

export function distributeDocumentAmount(
  amount: unknown,
  orders: readonly Order[],
): DocumentShare[] {
  const total = Math.max(0, Number(amount) || 0);
  const weights = orders.map((order) => Math.max(0, orderTotal(order)));
  const weightTotal = weights.reduce((sum, value) => sum + value, 0);
  let used = 0;
  return orders.map((order, index) => {
    const share =
      index === orders.length - 1
        ? Math.max(0, Math.round((total - used) * 100) / 100)
        : Math.round(
            (total * (weightTotal > 0 ? weights[index] / weightTotal : 1 / Math.max(orders.length, 1))) *
              100,
          ) / 100;
    used += share;
    return { orderId: order.id, amount: share };
  });
}

// ================= documentItemAllocations (index.html:12266-12297) =================

/** Eine Zeile aus `documentItemAllocations` - Reihenfolge wie im Original-Objektliteral. */
export interface DocumentAllocationRow {
  orderId: unknown;
  itemIndex: number;
  amount: number;
  matched: boolean;
}

/** `DB.orders` (ueber `byId`) ist hier der vierte Parameter `alleBestellungen`. */
export function documentItemAllocations(
  amount: unknown,
  orders: readonly Order[],
  parsedItems: readonly EkItem[] = [],
  alleBestellungen: readonly Order[] = [],
): DocumentAllocationRow[] {
  const normalizeKey = (value: unknown): string =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
  const orderShares = distributeDocumentAmount(amount, orders);
  const parsed = (parsedItems || [])
    .map((item) => ({
      key: normalizeKey(item.sku || item.name),
      amount: Math.max(0, Number(item.sourceAmount) || (Number(item.qty) || 0) * (Number(item.price) || 0)),
    }))
    .filter((item) => item.key && item.amount > 0);
  const rows: DocumentAllocationRow[] = [];
  for (const share of orderShares) {
    const order = byId(share.orderId, alleBestellungen);
    const items = ((order?.items || []) as EkItem[])
      .map((item, itemIndex) => ({ item, itemIndex }))
      .filter((entry) => !entry.item.manual && !entry.item.foc);
    const weights = items.map((entry) =>
      Math.max(0, (Number(entry.item.qty) || 0) * (Number(entry.item.price) || 0)),
    );
    const weightTotal = weights.reduce((sum, value) => sum + value, 0);
    let used = 0;
    items.forEach((entry, index) => {
      const itemKey = normalizeKey(entry.item.sku || entry.item.name);
      const exact = parsed.find(
        (candidate) =>
          candidate.key === itemKey || candidate.key.includes(itemKey) || itemKey.includes(candidate.key),
      );
      const amountForItem =
        index === items.length - 1
          ? Math.max(0, Math.round((Number(share.amount) - used) * 100) / 100)
          : Math.round(
              (Number(share.amount) *
                (weightTotal > 0 ? weights[index] / weightTotal : 1 / Math.max(items.length, 1))) *
                100,
            ) / 100;
      used += amountForItem;
      rows.push({
        orderId: (order as Order).id,
        itemIndex: entry.itemIndex,
        amount: amountForItem,
        matched: !!exact,
      });
    });
  }
  return rows;
}

// ================= consolidatedItems (index.html:8288-8306, unveraendert) =================

/** Herkunft eines konsolidierten Postens - Reihenfolge wie im Original-Objektliteral. */
export interface ConsolidatedSource {
  orderId: unknown;
  orderNo: unknown;
  qty: number;
}

/**
 * Eine konsolidierte Position. `[feld: string]: unknown` haelt die uebrigen, aus `cloneData(item)`
 * uebernommenen Positionsfelder fest (der Cast am Rueckgabewert unten haelt die tatsaechliche
 * Schluesselreihenfolge fest - erst die Positionsfelder, dann `qty`/`consolidatedSources`).
 */
export interface ConsolidatedItem {
  [feld: string]: unknown;
  qty: string;
  consolidatedSources: ConsolidatedSource[];
}

export function consolidatedItems(orders: readonly Order[]): ConsolidatedItem[] {
  const rows: ConsolidatedItem[] = [];
  const byKey = new Map<string, number>();
  for (const order of orders) {
    for (const item of (order.items || []) as EkItem[]) {
      const key = [normSearch(item.sku || item.name), Number(item.price) || 0, !!item.manual, !!item.foc].join(
        "|",
      );
      const qty = Number(item.qty) || 0;
      if (key.split("|")[0] && byKey.has(key)) {
        const existing = rows[byKey.get(key) as number];
        existing.qty = String((Number(existing.qty) || 0) + qty);
        existing.consolidatedSources.push({ orderId: order.id, orderNo: order.orderNo || "", qty });
      } else {
        const next = {
          ...(cloneData(item) as Record<string, unknown>),
          qty: String(qty),
          consolidatedSources: [{ orderId: order.id, orderNo: order.orderNo || "", qty }],
        } as ConsolidatedItem;
        byKey.set(key, rows.length);
        rows.push(next);
      }
    }
  }
  return rows;
}

// ================= articleStats (index.html:14216-14253) =================

/** Eintrag der Artikelhistorie - Bestandteil von `ArticleStatsRow.entries`/`lastPrices`. */
export interface ArticleStatsEntry {
  order: Order;
  qty: number;
  price: number;
  date: unknown;
}

/** Ergebniszeile von `articleStats` - Reihenfolge wie im Original-Objektliteral (14242-14252). */
export interface ArticleStatsRow {
  name: string;
  sku: unknown;
  entries: ArticleStatsEntry[];
  orders: number;
  totQty: number;
  avg: number;
  min: number;
  max: number;
  lastPrices: ArticleStatsEntry[];
  lastPrice: number;
  lastOrder: Order | null;
}

/**
 * index.html:6612, modulintern hierher kopiert statt aus `lib/status.ts` importiert: die
 * Statusbereinigung hat den dortigen gemeinsamen Export entfernt (der Board-Status "split", auf den
 * er prueft, existiert in der LIVE-Konfiguration nicht mehr), `articleStats` unten ist aber eine
 * woertliche 1:1-Portierung von index.html:14216-14253 und muss GENAU deren dort unveraendert
 * gebliebenes Ausschlussverhalten replizieren (siehe Kopfkommentar oben). Bewusst nicht exportiert -
 * derselbe Ansatz wie bei den "notwendigen Abhaengigkeiten" in `lib/status.ts` (dortiger
 * Kopfkommentar).
 */
function isSplitStatusOrderPortiert(
  order: Order | null | undefined,
  definitionen: StatusDefinitionen,
): boolean {
  return (
    order?.status === "split" ||
    normSearch(definitionen.STATUS_MAP[order?.status as string]?.label) === "split"
  );
}

/** `DB.orders` ist hier `orders`; `isSplitStatusOrderPortiert` braucht zusaetzlich `definitionen`. */
export function articleStats(
  orders: readonly Order[],
  definitionen: StatusDefinitionen,
): ArticleStatsRow[] {
  const map = new Map<string, { name: string; sku: unknown; entries: ArticleStatsEntry[] }>();
  for (const o of orders) {
    if (isSplitStatusOrderPortiert(o, definitionen)) continue;
    for (const it of (o.items || []) as EkItem[]) {
      const name = ((it.name || "") as string).trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (!map.has(key)) map.set(key, { name, sku: it.sku || "", entries: [] });
      const g = map.get(key) as { name: string; sku: unknown; entries: ArticleStatsEntry[] };
      if (!g.sku && it.sku) g.sku = it.sku;
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      if (qty > 0 || price > 0) {
        g.entries.push({ order: o, qty, price, date: o.orderDate || o.createdAt });
      }
    }
  }
  const rows: ArticleStatsRow[] = [];
  for (const g of map.values()) {
    const totQty = g.entries.reduce((s, e) => s + e.qty, 0);
    const totVal = g.entries.reduce((s, e) => s + e.qty * e.price, 0);
    const prices = g.entries.filter((e) => e.price > 0);
    // Im Original eine Date-Subtraktion (`new Date(...) - new Date(...)`); TypeScript erlaubt die
    // implizite Zahl-Koerzion zweier Date-Objekte nicht. `.getTime()` liefert denselben Zahlenwert
    // wie die von `valueOf()` erzwungene Original-Subtraktion - reine Typergaenzung.
    const last = g.entries
      .slice()
      .sort(
        (a, b) =>
          new Date(b.date as string | number).getTime() - new Date(a.date as string | number).getTime(),
      )[0];
    const lastPrices = prices
      .slice()
      .sort(
        (a, b) =>
          new Date(b.date as string | number).getTime() - new Date(a.date as string | number).getTime(),
      )
      .slice(0, 3);
    rows.push({
      ...g,
      orders: g.entries.length,
      totQty,
      avg:
        totQty > 0
          ? totVal / totQty
          : prices.length
            ? prices.reduce((s, e) => s + e.price, 0) / prices.length
            : 0,
      min: prices.length ? Math.min(...prices.map((e) => e.price)) : 0,
      max: prices.length ? Math.max(...prices.map((e) => e.price)) : 0,
      lastPrices,
      lastPrice: last ? last.price : 0,
      lastOrder: last ? last.order : null,
    });
  }
  return rows.sort((a, b) => a.name.localeCompare(b.name, "de"));
}
